/**
 * Insider Integration — Event Tracking
 * Uses InsiderQueue pattern with custom_event type.
 */

import { insiderConfig } from './insider.config';
import { queuePush, queueCommit } from './insider';
import { EInsiderQueueType } from './insider.types';
import type { RegistrationEventParams, ReservationEventParams } from './insider.types';

// Dedup sets — prevent double-click / StrictMode duplicates
const sentRegistrations = new Set<string>();
const sentReservations = new Set<string>();

function formatRedemptionDate(date: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : date;
}

/**
 * Track campaign-voucher_registration event.
 */
export function trackVoucherRegistration(params: RegistrationEventParams): void {
  if (!insiderConfig.enabled) return;
  if (sentRegistrations.has(params.cpf_hash)) return;
  sentRegistrations.add(params.cpf_hash);

  queuePush({
    type: EInsiderQueueType.custom_event,
    value: [{
      event_name: 'campaign-voucher_registration',
      event_parameters: {
        cpf_hash: params.cpf_hash,
        campaign_name: params.campaign_name,
        consent_terms: params.consent_terms,
        consent_notification: params.consent_notification,
        event_date: new Date().toISOString(),
      },
    }],
  });
  queueCommit();
}

/**
 * Track campaign-voucher_reservation event.
 */
export function trackVoucherReservation(params: ReservationEventParams): void {
  if (!insiderConfig.enabled) return;
  if (sentReservations.has(params.voucher_id)) return;
  sentReservations.add(params.voucher_id);

  queuePush({
    type: EInsiderQueueType.custom_event,
    value: [{
      event_name: 'campaign-voucher_reservation',
      event_parameters: {
        c_voucher_id: params.voucher_id,
        c_reservation_date: params.reservation_date,
        redemption_date: formatRedemptionDate(params.redemption_date),
        c_redemption_datetime: new Date(params.redemption_date).toISOString(),
        c_store_code: params.store_code,
        c_store_name: params.store_name,
        campaign_name: params.campaign_name,
        event_date: new Date().toISOString(),
      },
    }],
  });
  queueCommit();
}
